import { DEVICE_ID_COOKIE_FIELD } from "@finway-group/shared/lib/consts/common"
import { type AccessTokenType, CollectionNameEnum, type DocumentChangeInterface } from "@finway-group/shared/lib/models"
import { Cookies } from "react-cookie"
import io, { type Socket } from "socket.io-client"
import { PROCESS_ENV } from "~Shared/config/env.utils"
import { invalidateNotifications } from "~Shared/queries/notification.queries"
import { AuthnService, LocalStorageService } from "~Shared/services"
import { subscribeToAuth } from "~Shared/services/socketSync/authSocket.service"
import { subscribeToBankConnection } from "~Shared/services/socketSync/bankConnectionSocket.service"
import { subscribeToCompanySettings, subscribeToCompanySettingsCallbacks } from "~Shared/services/socketSync/companySettingsSocket.service"
import { subscribeToCorporate } from "~Shared/services/socketSync/corporateSocket.service"
import { subscribeToCostCenter2 } from "~Shared/services/socketSync/costCenter2Socket.service"
import { subscribeToCostCenter } from "~Shared/services/socketSync/costCenterSocket.service"
import { subscribeToCreditCardCallbacks, subscribeToCreditCards } from "~Shared/services/socketSync/creditCardSocket.service"
import { subscribeToCsvTemplate } from "~Shared/services/socketSync/csvTemplatesSocket.service"
import { subscribeToCurrencyExchange } from "~Shared/services/socketSync/currencyExchangeSocket.service"
import { subscribeToDeliveryNotes } from "~Shared/services/socketSync/deliveryNoteSocket.service"
import { refreshForMySubstitutionUpdate, subscribeToEmployee } from "~Shared/services/socketSync/employeeSocket.service"
import { subscribeToExpenseAccount } from "~Shared/services/socketSync/expenseAccountSocket.service"
import { subscribeToExpenseCallbacks, subscribeToExpenses } from "~Shared/services/socketSync/expenseSocket.service"
import { subscribeToExpenseTag } from "~Shared/services/socketSync/expenseTagSocket.service"
import { subscribeToExportHistoryCallbacks } from "~Shared/services/socketSync/exportHistorySocket.service"
import { isIncomingFilterEqualToLocal, subscribeToFilter } from "~Shared/services/socketSync/filterSocket.service"
import { subscribeToImportResult } from "~Shared/services/socketSync/importResultSocket.service"
import { subscribeToInboxInvoiceCallbacks, subscribeToInboxInvoices } from "~Shared/services/socketSync/inboxInvoiceSocket.service"
import { subscribeToPaymentSettings } from "~Shared/services/socketSync/paymentSettingsSocket.service"
import { subscribeToPurchaseOrder, subscribeToPurchaseOrderCallbacks } from "~Shared/services/socketSync/purchaseOrderSocket.service"
import { subscribeToRole } from "~Shared/services/socketSync/roleSocket.service"
import { subscribeToTax } from "~Shared/services/socketSync/taxSocket.service"
import { subscribeToTeam } from "~Shared/services/socketSync/teamSocket.service"
import { subscribeToTenant } from "~Shared/services/socketSync/tenantSocket.service"
import { subscribeToTransactionCategory, subscribeToTransactionCategoryCallbacks } from "~Shared/services/socketSync/transactionCategorySocket.service"
import { subscribeToTransactions, subscribeToTransactionsCallbacks } from "~Shared/services/socketSync/transactionSocket.service"
import { subscribeToVendor, subscribeToVendorCallbacks } from "~Shared/services/socketSync/vendorSocket.service"
import { subscribeToWallet } from "~Shared/services/socketSync/walletSocket.service"
import { subscribeToWorkflow } from "~Shared/services/socketSync/workflowSocket.service"
import { store } from "~Shared/store"
import { updateAnalytics } from "~Shared/store/actions/analytics/analyticsActions"
import { delay } from "~Shared/utils/helper.utils"
import { isNotification } from "~Shared/utils/navbarActivityTemplates.utils"

class SocketService {
    private socket: typeof Socket
    private onSuccessInstance?: () => void
    private onErrorInstance?: (state: string) => void

    private loggedInUserId = ""
    private isReconnecting = false
    private location = ""

    private dispatch = store.dispatch

    public connect({ loggedInUserId, onError, onSuccess }: { loggedInUserId: string; onError?: (state: string) => void; onSuccess?: () => void }) {
        this.loggedInUserId = loggedInUserId
        this.onErrorInstance = onError
        this.onSuccessInstance = onSuccess

        console.log(`[Socket] Connecting: socketId=${this.socket?.id} userId=${this.loggedInUserId}`)

        const self = this
        let connection: any
        if (!this.socket || !this.socket.connected) {
            connection = io.connect(`${PROCESS_ENV.API_SYNC_URL}/dashboard`, {
                query: `auth_token=${localStorage.accessToken}`,
                transports: ["websocket"],
                secure: true,
                timeout: 60000,
            })

            connection.on("reconnect_attempt", () => {
                connection.io.opts.transports = ["websocket", "polling"]
            })

            connection.on("connect_error", (err: any) => {
                console.warn(`[Socket] Event connect_error: socketId=${connection.id} userId=${loggedInUserId}: ${err}`)

                onError?.(err)
            })

            connection.on("connect_failed", (err: any) => {
                console.warn(`[Socket] Event connect_failed reconnecting: socketId=${connection.id} userId=${loggedInUserId}`)

                onError?.(err)
            })

            connection.on("reconnect_error", (err: any) => {
                console.warn(`[Socket] Event reconnect_error retry in a couple seconds: socketId=${connection.id} userId=${loggedInUserId}: ${err}`)

                onError?.(err)
            })

            connection.on("connect", () => {
                console.debug(`[Socket] Event connect socketId=${connection.id} userId=${loggedInUserId} `)
                self.socket = connection
                onSuccess?.()
            })

            // Connection succeeded
            connection.on("success", (data: { message: string; user: AccessTokenType }) => {
                console.debug(`[Socket] Event success socketId=${connection.id} userId=${loggedInUserId}: ${data.message}`)
                self.socket = connection
                onSuccess?.()
                self.subscribeToChange()
            })

            connection.on("error", async (err: { [key: string]: string }) => {
                onError?.(err.message)
                if (LocalStorageService.isAuthorized() && err.message === "Token expired") {
                    if (!this.isReconnecting) {
                        this.isReconnecting = true
                        await self.reconnect({ refreshToken: true })
                    }
                    return
                }

                connection.disconnect()
            })

            connection.on("disconnect", () => {
                if (self.socket?.nsp && !LocalStorageService.isAuthorized()) {
                    self.disconnect("LoggingOut")
                    return
                }

                onError?.("disconnected")
            })
        } else if (this.socket.connected) {
            console.log(`[Socket] Connecting: status=${self.socket.connected}`)
            onSuccess?.()
        }

        return connection
    }

    // Check if socket reconnection is firing then delete this
    reconnect = async ({ refreshToken }: { refreshToken: boolean }) => {
        if (!LocalStorageService.isAuthorized()) {
            this.disconnect("No Access Token")
            return
        }

        console.log(`[Socket] Reconnecting: socketId=${this.socket?.id} userId=${this.loggedInUserId} refreshToken=${refreshToken}`)

        if (refreshToken) {
            await AuthnService.silentRefresh()
        }
        await delay(3000)
        this.socket?.connect()
        await delay(2000)
        if (!this.socket?.connected) {
            this.connect({ loggedInUserId: this.loggedInUserId, onError: this.onErrorInstance, onSuccess: this.onSuccessInstance })
        }

        await delay(2000)
        this.isReconnecting = false
    }

    setLocation = (location: string) => {
        this.location = location
    }

    checkIsRealTimeHandlingNeeded = async (fullDocument: DocumentChangeInterface["fullDocument"]): Promise<boolean> => {
        const shouldFetchActivities = isNotification(fullDocument)
        const { documentId, deviceId } = fullDocument

        const cookies = new Cookies(document.cookie)
        const currentDeviceId = cookies.get(DEVICE_ID_COOKIE_FIELD)

        const isActivityNotFromCurrentDevice = deviceId !== currentDeviceId
        const isAboutMe = this.loggedInUserId === documentId
        if (isActivityNotFromCurrentDevice || isAboutMe) {
            if (shouldFetchActivities) {
                await invalidateNotifications()
            }
        }

        return isActivityNotFromCurrentDevice || isAboutMe
    }

    isMyFilterUpdate = (data: any) => this.loggedInUserId === data.fullDocument.metaData.updatedFields.user

    subscribeToChange() {
        const subscribeToExpenseOrSubscription = async (data: DocumentChangeInterface) => {
            if (await this.checkIsRealTimeHandlingNeeded(data.fullDocument)) {
                void subscribeToExpenses(this.dispatch, data)
            } else {
                subscribeToExpenseCallbacks(this.dispatch, data)
            }
        }

        // socket.io/aws idle timeout handler
        this.socket.on("ping", (data: any) => {
            console.debug(`[Socket] heartbeat: ${JSON.stringify(data)}`)
            this.socket.emit("pong", data)
        })

        this.socket.on(CollectionNameEnum.EXPENSE, async (data: DocumentChangeInterface) => {
            await subscribeToExpenseOrSubscription(data)
        })
        this.socket.on(CollectionNameEnum.SUBSCRIPTION, async (data: DocumentChangeInterface) => {
            await subscribeToExpenseOrSubscription(data)
        })

        this.socket.on(CollectionNameEnum.MATCHING_TRANSACTION, async (data: DocumentChangeInterface) => {
            if (await this.checkIsRealTimeHandlingNeeded(data.fullDocument)) {
                subscribeToTransactions(this.dispatch, data)
            } else {
                subscribeToTransactionsCallbacks(this.dispatch, data)
            }
        })

        this.socket.on(CollectionNameEnum.EXPENSE_ACCOUNT, async (data: DocumentChangeInterface) => {
            if (await this.checkIsRealTimeHandlingNeeded(data.fullDocument)) {
                subscribeToExpenseAccount(this.dispatch, data)
            }
        })

        this.socket.on(CollectionNameEnum.COST_CENTER_2, async (data: DocumentChangeInterface) => {
            if (await this.checkIsRealTimeHandlingNeeded(data.fullDocument)) {
                subscribeToCostCenter2(this.dispatch, data)
            }
        })

        this.socket.on(CollectionNameEnum.USER, async (data: DocumentChangeInterface) => {
            const shouldDisconnect = subscribeToAuth(data, this.loggedInUserId)
            if (shouldDisconnect) {
                this.socket.emit("disconnect")
                return
            }

            await refreshForMySubstitutionUpdate(this.dispatch, data, this.loggedInUserId)
            if (await this.checkIsRealTimeHandlingNeeded(data.fullDocument)) {
                void subscribeToEmployee(this.dispatch, data, this.loggedInUserId)
            }
        })

        this.socket.on(CollectionNameEnum.VENDOR, async (data: DocumentChangeInterface) => {
            if (await this.checkIsRealTimeHandlingNeeded(data.fullDocument)) {
                await subscribeToVendor(this.dispatch, data)
            } else {
                await subscribeToVendorCallbacks(this.dispatch, data)
            }
        })

        this.socket.on(CollectionNameEnum.COST_CENTER, async (data: DocumentChangeInterface) => {
            if (await this.checkIsRealTimeHandlingNeeded(data.fullDocument)) {
                void subscribeToCostCenter(this.dispatch, data)
            }
        })

        this.socket.on(CollectionNameEnum.COMPANY_SETTINGS, async (data: DocumentChangeInterface) => {
            if (await this.checkIsRealTimeHandlingNeeded(data.fullDocument)) {
                subscribeToCompanySettings(this.dispatch, data)
            } else {
                subscribeToCompanySettingsCallbacks(this.dispatch, data)
            }
        })

        this.socket.on(CollectionNameEnum.BANK_CONNECTION, (data: DocumentChangeInterface) => {
            subscribeToBankConnection(this.dispatch, data, this.loggedInUserId)
        })

        this.socket.on(CollectionNameEnum.PAYMENT_SETTINGS, async (data: DocumentChangeInterface) => {
            await subscribeToPaymentSettings(this.dispatch, data)
        })

        this.socket.on(CollectionNameEnum.CURRENCY_EXCHANGE, async (data: DocumentChangeInterface) => {
            if (await this.checkIsRealTimeHandlingNeeded(data.fullDocument)) {
                subscribeToCurrencyExchange(this.dispatch, data)
            }
        })

        this.socket.on(CollectionNameEnum.CARD, async (data: DocumentChangeInterface) => {
            if (await this.checkIsRealTimeHandlingNeeded(data.fullDocument)) {
                subscribeToCreditCards(this.dispatch, data, this.loggedInUserId)
            } else {
                subscribeToCreditCardCallbacks(this.dispatch, data)
            }
        })

        this.socket.on(CollectionNameEnum.WALLET, async (data: DocumentChangeInterface) => {
            if (await this.checkIsRealTimeHandlingNeeded(data.fullDocument)) {
                subscribeToWallet(this.dispatch)
            }
        })

        this.socket.on(CollectionNameEnum.CORPORATE, async (data: DocumentChangeInterface) => {
            if (await this.checkIsRealTimeHandlingNeeded(data.fullDocument)) {
                subscribeToCorporate(this.dispatch, data)
            }
        })

        this.socket.on(CollectionNameEnum.INBOX_INVOICE, async (data: DocumentChangeInterface) => {
            if (await this.checkIsRealTimeHandlingNeeded(data.fullDocument)) {
                subscribeToInboxInvoices(this.dispatch, data)
            } else {
                subscribeToInboxInvoiceCallbacks(this.dispatch, data)
            }
        })

        this.socket.on(CollectionNameEnum.TEAM, async (data: DocumentChangeInterface) => {
            if (await this.checkIsRealTimeHandlingNeeded(data.fullDocument)) {
                subscribeToTeam(this.dispatch, data)
            }
        })

        this.socket.on(CollectionNameEnum.TAX, async (data: DocumentChangeInterface) => {
            if (await this.checkIsRealTimeHandlingNeeded(data.fullDocument)) {
                subscribeToTax(this.dispatch, data)
            }
        })

        this.socket.on(CollectionNameEnum.WORKFLOW, async (data: DocumentChangeInterface) => {
            if (await this.checkIsRealTimeHandlingNeeded(data.fullDocument)) {
                void subscribeToWorkflow(this.dispatch, data)
            }
        })

        this.socket.on(CollectionNameEnum.IMPORT_RESULT, (data: DocumentChangeInterface) => {
            if (this.location === "/reports") {
                this.dispatch(updateAnalytics())
            }
            void subscribeToImportResult(this.dispatch, data, this.loggedInUserId)
        })

        this.socket.on(CollectionNameEnum.TRANSACTION_CATEGORY, async (data: DocumentChangeInterface) => {
            if (await this.checkIsRealTimeHandlingNeeded(data.fullDocument)) {
                void subscribeToTransactionCategory(this.dispatch, data)
            } else {
                void subscribeToTransactionCategoryCallbacks(this.dispatch, data)
            }
        })

        this.socket.on(CollectionNameEnum.EXPENSE_TAG, async (data: DocumentChangeInterface) => {
            if (await this.checkIsRealTimeHandlingNeeded(data.fullDocument)) {
                subscribeToExpenseTag(this.dispatch, data)
            }
        })

        this.socket.on(CollectionNameEnum.ROLE, async (data: DocumentChangeInterface) => {
            if (await this.checkIsRealTimeHandlingNeeded(data.fullDocument)) {
                void subscribeToRole(this.dispatch, data)
            }
        })

        this.socket.on(CollectionNameEnum.CSV_TEMPLATE, async (data: DocumentChangeInterface) => {
            if (await this.checkIsRealTimeHandlingNeeded(data.fullDocument)) {
                subscribeToCsvTemplate(this.dispatch, data)
            }
        })

        this.socket.on(CollectionNameEnum.FILTER, (data: any) => {
            if (this.isMyFilterUpdate(data) && !isIncomingFilterEqualToLocal(data)) {
                subscribeToFilter(data)
            }
        })
        this.socket.on(CollectionNameEnum.EXPORT_HISTORY, async (data: DocumentChangeInterface) => {
            // ! this uses only the callbacks (bc all export history objects are user specific)
            if (!(await this.checkIsRealTimeHandlingNeeded(data.fullDocument))) {
                subscribeToExportHistoryCallbacks(this.dispatch, data)
            }
        })
        this.socket.on(CollectionNameEnum.TENANT, (data: DocumentChangeInterface) => {
            subscribeToTenant(data)
        })

        this.socket.on(CollectionNameEnum.PURCHASE_ORDER, async (data: DocumentChangeInterface) => {
            if (await this.checkIsRealTimeHandlingNeeded(data.fullDocument)) {
                await subscribeToPurchaseOrder(data)
            } else {
                await subscribeToPurchaseOrderCallbacks(data)
            }
        })

        this.socket.on(CollectionNameEnum.DELIVERY_NOTE, async (data: DocumentChangeInterface) => {
            await subscribeToDeliveryNotes(this.dispatch, data)
        })
    }

    // disconnect - used when unmounting
    public disconnect(reason: any) {
        console.warn(`[Socket] Disconnecting: reason=${reason}`)

        this.socket?.removeAllListeners()
        this.socket?.disconnect()
        this.socket?.close()
    }
}

export const socketService = new SocketService()
